"use client";

import React, { useState } from "react";
import { GripVertical, Maximize2, MapPin, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
const galleryItems = [{
  id: 1,
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636402/img/gallery_garage_before.png",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636402/img/gallery_garage_after.png",
  title: "Private Ledger Interface",
  neighborhood: "Secure Architecture",
  description: "Replace cluttered transaction histories with a clean, private ledger. Every entry is tenant-isolated and encrypted, providing a surgical view of your digital assets without exposing your identity to public trackers.",
  aspectRatio: "aspect-[4/3]"
}, {
  id: 2,
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636402/img/gallery_yard_before.png",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636402/img/gallery_yard_after.png",
  title: "AI Commerce Agent",
  neighborhood: "Intelligent Automation",
  description: "Our AI agents handle the complexity of cross-chain swaps and merchant approvals. What used to take multiple manual steps is now a single-thread confirmation within your preferred workspace or chat channel.",
  aspectRatio: "aspect-[3/4]"
}, {
  id: 3,
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636402/img/gallery_office_before.png",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636402/img/gallery_office_after.png",
  title: "Managed Access Control",
  neighborhood: "OceanClaw Security",
  description: "Upgrade from chaotic API keys to managed capability. Define entitlements and policy controls through a centralized dashboard that routes all access through our high-performance control plane.",
  aspectRatio: "aspect-square"
}, {
  id: 4,
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636402/img/gallery_appliance_before.png",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636402/img/gallery_appliance_after.png",
  title: "Apple Pay Integration",
  neighborhood: "Seamless Checkout",
  description: "The bridge between traditional finance and crypto. We've integrated WordPress and Apple Pay to make onboarding and purchasing as simple as a double-click, removing all technical friction from the process.",
  aspectRatio: "aspect-[4/3]"
}, {
  id: 5,
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636402/img/gallery_basement_before.png",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636402/img/gallery_basement_after.png",
  title: "iMessage Onboarding",
  neighborhood: "Familiar Experience",
  description: "No new apps to learn for basic interactions. Our private-thread experience allows you to manage requests and approvals directly within iMessage or Slack, keeping your workflow unified and immediate.",
  aspectRatio: "aspect-[3/4]"
}, {
  id: 6,
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636402/img/gallery_construction_before.png",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636402/img/gallery_construction_after.png",
  title: "Tenant Workspace",
  neighborhood: "Identity Management",
  description: "Every signup creates a dedicated tenant environment. This ensures your identity, entitlements, and channel bindings are strictly controlled and isolated from other network participants for total privacy.",
  aspectRatio: "aspect-video"
}];
function BeforeAfterCard({
  item,
  index
}: {
  item: typeof galleryItems[0];
  index: number;
}) {
  const [position, setPosition] = useState(50);
  return <div data-section-id="225017" data-index={index} className="break-inside-avoid mb-6">
      <Dialog>
        <Card className="overflow-hidden border-2 border-border/40 rounded-xl shadow-2xl group bg-card/30 backdrop-blur-xl transition-all hover:border-primary/50">
          <div className={`relative w-full ${item.aspectRatio} bg-muted`}>
            {}
            <img src={item.afterImage} alt={`After ${item.title}`} className="absolute inset-0 w-full h-full object-cover" />

            {}
            <img src={item.beforeImage} alt={`Before ${item.title}`} className="absolute inset-0 w-full h-full object-cover" style={{
            clipPath: `polygon(0 0, ${position}% 0, ${position}% 100%, 0 100%)`
          }} />

            {}
            <div className="absolute top-0 bottom-0 w-0.5 bg-primary/80 pointer-events-none z-10" style={{
            left: `${position}%`
          }}>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-background/80 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center shadow-4xl">
                <GripVertical className="size-5 text-primary" />
              </div>
            </div>

            {}
            <input type="range" min="0" max="100" value={position} onChange={e => setPosition(Number(e.target.value))} className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-20" aria-label="Drag to compare before and after" />

            {}
            <div className="absolute top-4 right-4 z-30 opacity-0 group-hover:opacity-100 transition-opacity focus-within:opacity-100">
              <DialogTrigger asChild>
                <Button variant="secondary" size="icon" className="rounded-full border border-white/10 bg-black/40 backdrop-blur-md shadow-lg hover:bg-primary hover:text-white transition-colors">
                  <Maximize2 className="size-4" />
                  <span className="sr-only">View Details for {item.title}</span>
                </Button>
              </DialogTrigger>
            </div>
            
            {}
            <div className="absolute bottom-4 left-4 z-10 pointer-events-none">
              <span className="bg-black/60 backdrop-blur-md text-white/70 text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/10">
                Legacy
              </span>
            </div>
            <div className="absolute bottom-4 right-4 z-10 pointer-events-none">
              <span className="bg-primary/80 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/20 shadow-lg">
                OceanWallet
              </span>
            </div>
          </div>

          <CardContent className="p-6 border-t border-border/30">
            <h3 className="font-default text-xl font-bold text-foreground mb-2 leading-tight tracking-tight">
              {item.title}
            </h3>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 font-medium tracking-wide">
              <MapPin className="size-3.5 text-primary" /> {item.neighborhood}
            </p>
          </CardContent>
        </Card>

        <DialogContent className="max-w-5xl border border-white/10 rounded-2xl p-0 overflow-hidden gap-0 bg-background/95 backdrop-blur-3xl shadow-4xl sm:rounded-2xl">
          <div className="grid md:grid-cols-5 h-full max-h-[90vh] overflow-y-auto md:overflow-hidden">
            {}
            <div className="relative md:col-span-3 bg-muted min-h-[400px] md:min-h-0">
              <img src={item.afterImage} alt={`After ${item.title}`} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute top-8 left-8 bg-black/60 backdrop-blur-xl px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] border border-white/10 rounded-full shadow-2xl flex items-center gap-3 text-white">
                <Sparkles className="size-4 text-primary animate-pulse" />
                The OceanWallet Standard
              </div>
            </div>
            
            {}
            <div className="p-10 flex flex-col bg-card/20 md:col-span-2 border-t md:border-t-0 md:border-l border-white/5 overflow-y-auto">
              <DialogHeader className="mb-8 text-left space-y-4">
                <DialogTitle className="font-default text-4xl font-bold leading-tight text-foreground tracking-tighter">
                  {item.title}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-2 text-sm text-primary font-bold tracking-widest uppercase">
                  <MapPin className="size-4" /> {item.neighborhood}
                </DialogDescription>
              </DialogHeader>
              
              <div className="prose prose-sm prose-invert mb-10">
                <p className="text-muted-foreground text-lg leading-relaxed font-medium">
                  {item.description}
                </p>
              </div>
              
              <div className="mt-auto pt-8 border-t border-white/5">
                <h4 className="text-[10px] font-black text-muted-foreground mb-4 uppercase tracking-[0.3em] flex items-center gap-2">
                  Legacy Friction
                </h4>
                <div className="relative aspect-video rounded-xl border border-white/5 overflow-hidden shadow-inner bg-black">
                  <img src={item.beforeImage} alt={`Before ${item.title}`} className="w-full h-full object-cover grayscale opacity-40 hover:grayscale-0 hover:opacity-100 transition-all duration-700" />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>;
}
export default function TransformationGallery() {
  return <section id="coastal-tech-trust" className="py-32 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_50%_0%,hsl(210,100%,10%),transparent_50%)] pointer-events-none" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-20">
          <h2 className="font-default text-5xl md:text-6xl font-black text-foreground mb-8 tracking-tighter">
            Visual Proof of <span className="text-primary">Intelligence</span>
          </h2>
          <p className="text-xl text-muted-foreground font-medium max-w-2xl mx-auto leading-relaxed">
            Drag the sliders to experience the surgical precision of OceanWallet. We transform complex crypto workflows into elegant, managed digital experiences.
          </p>
        </div>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-8">
          {galleryItems.map((item, i) => <BeforeAfterCard key={item.id} item={item} index={i} />)}
        </div>
        
        <div className="mt-20 text-center">
           <Button size="lg" className="font-bold text-lg px-10 py-8 rounded-2xl bg-primary text-white hover:bg-primary/90 hover:scale-[1.02] transition-all shadow-xl shadow-primary/20 tracking-tight" asChild>
             <a href="/OceanWalletPage">Download on the App Store</a>
           </Button>
           <p className="mt-6 text-sm font-bold text-muted-foreground uppercase tracking-[0.2em]">
             Immediate Access via Apple Pay
           </p>
        </div>
      </div>
    </section>;
}